import http, { endpoints } from '../http';
import { mapUserBranchAccessFromDto, mapUserFromDto } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
/**
 * Mostly EFKT-MOCK. `getUserById` is real — the Efkt fe-gateway serves `GET /User/id/{id}` in
 * retail-core's own shape (coverage row 1.5, merged 2026-09-10). Everything else here has no Efkt
 * endpoint at all: the per-branch user list is coverage row 2.1 (open) and the whole invitation
 * lifecycle is row 2.3 (blocked — accepting an invite means creating a B2C account, and no deployed
 * environment has a customer directory to create one in). Those paths are unconfirmed guesses; grep
 * `EFKT-MOCK` across this package for every placeholder.
 *
 * `getContactPersonTypes` is absent on purpose — it already has a real Efkt equivalent,
 * `ContactsApi.getContactTypes` (`GET /api/contact-types`).
 */
export default class UsersApi {
    /**
     * EFKT-MOCK — guessed, and the gap is real: Efkt has contacts synced from CRM as data but no
     * "users of a branch" read, and nothing carrying the admin/user access flag per member. Blocks
     * `/active-directory`'s member picker against Efkt.
     */
    static async getUsersForBranch(branchId) {
        const response = await efktClient.get(`/api/branch/${encodeURIComponent(branchId)}/users`);
        return response.data.map(dto => mapUserBranchAccessFromDto(dto, branchId));
    }
    /**
     * `GET /User/id/{id}`. **Own id only** — Efkt answers 403 for anyone else's, where retail-core let
     * any signed-in user read any id, and 404 for an authenticated caller who is not enrolled. So
     * this serves the session profile and the "my account" screen; the users-administration list
     * cannot read a colleague's row through it.
     */
    static async getUserById(id) {
        const response = await efktClient.get(`/User/id/${encodeURIComponent(id)}`);
        return mapUserFromDto(response.data);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.updateUser`. */
    static async updateUser(userId, params) {
        const body = params;
        await efktClient.patch(`/api/user/${encodeURIComponent(userId)}`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.getPendingInvitesForBranch`. */
    static async getPendingInvitesForBranch(branchId) {
        const response = await efktClient.get(`/api/branch/${encodeURIComponent(branchId)}/invitations`);
        return response.data;
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.sendInvite`. */
    static async sendInvite({ email, branches }) {
        const body = { email, branches };
        await efktClient.post('/api/invitations', body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.removePendingInvite`. */
    static async removePendingInvite(inviteId) {
        await efktClient.delete(`/api/invitation/${encodeURIComponent(inviteId)}`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.resendInvite`. */
    static async resendInvite(inviteId) {
        await efktClient.post(`/api/invitation/${encodeURIComponent(inviteId)}/resend`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.findInviteById`. */
    static async findInviteById(inviteId) {
        const response = await efktClient.get(`/api/invitation/${encodeURIComponent(inviteId)}`);
        return response.data;
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.acceptInvite`. */
    static async acceptInvite({ inviteId, firstName, lastName, mobilePhone, password }) {
        const body = { firstName, lastName, mobilePhone, password };
        await efktClient.post(`/api/invitation/${encodeURIComponent(inviteId)}/accept`, body);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.revokeBranchAccessForUser`. */
    static async revokeBranchAccessForUser({ userId, branchId }) {
        await efktClient.delete(`/api/user/${encodeURIComponent(userId)}/branch/${encodeURIComponent(branchId)}`);
    }
    /** EFKT-MOCK — guessed. Mirrors datahub's `UsersApi.updateBranchAccessForUser`. */
    static async updateBranchAccessForUser({ userId, branchId, isAdmin }) {
        const body = { isAdmin: !!isAdmin };
        await efktClient.patch(`/api/user/${encodeURIComponent(userId)}/branch/${encodeURIComponent(branchId)}`, body);
    }
}
